import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useDeckCheckQuery,
  useProfileMeQuery,
  useCoachDeckMutation,
  useMeQuery,
} from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { RatingBar } from '@/components/cr/RatingBar';
import { CoachAnalysis } from '@/components/cr/CoachAnalysis';
import { coachAnalysisError } from '@/components/cr/coachError';
import { Paywall } from '@/components/cr/Paywall';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

/**
 * Deck-check screen — ATK/DEF/SYN/VER/BAL rating + tips for the linked
 * player's current deck. Tag resolution is implicit: use the linked
 * SavedPlayer; if absent, redirect to /cr/link.
 *
 * Calls /profile/me (already cached after the home loads) to read the
 * current deck, then /decks/check/:tag for the rating. Could be folded
 * into a single response if it ever becomes a perf concern; for now it
 * keeps the cards endpoint cacheable independently of the rating.
 */
export function CRDeckCheck() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const { data: profile, isLoading: profileLoading, error: profileError } =
    useProfileMeQuery();
  const { data: identity } = useMeQuery();
  const tag = profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';
  const { data, isLoading, error } = useDeckCheckQuery({ tag }, { skip: !tag });
  const [analyse, { data: analysisData, isLoading: analysing, error: analysisError }] =
    useCoachDeckMutation();

  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';

  const onAnalyse = () => {
    if (!tag) return;
    haptics.impact('medium');
    analyse({ tag }).catch(() => undefined);
  };

  if (profileLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('deckCheck.loading')}</div>;
  }
  if (profileError) {
    const code = isQbotApiError(profileError) ? profileError.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') return <Navigate to="/cr/link" replace />;
  }
  if (isLoading || !data) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('deckCheck.loading')}</div>;
  }
  if (error) {
    return <div className="px-4 py-6 text-sm text-rose-300">{t('deckCheck.error')}</div>;
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 py-4">
      <header>
        {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. L'archetipo e l'elisir qui sotto sono DATI e restano. */}
        <h1 className="sr-only">{t('deckCheck.title')}</h1>
        <p className="text-[11px] text-fg-muted">
          {data.archetype} · {t('deckCheck.avgElixir', { value: data.avgElixir.toFixed(1) })}
        </p>
      </header>

      <section className="rounded-xl bg-bg-secondary p-3">
        <DeckGrid cards={data.deck} avgElixir={data.avgElixir} />
      </section>

      <section className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
          {t('deckCheck.rating')}
        </h2>
        <RatingBar label={t('deckCheck.label.atk')} value={data.rating.attack} />
        <RatingBar label={t('deckCheck.label.def')} value={data.rating.defense} />
        <RatingBar label={t('deckCheck.label.syn')} value={data.rating.synergy} />
        <RatingBar label={t('deckCheck.label.ver')} value={data.rating.versatility} />
        <RatingBar label={t('deckCheck.label.bal')} value={data.rating.balance} />
      </section>

      {data.tips.length > 0 && (
        <section className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
            {t('deckCheck.tips')}
          </h2>
          <ul className="flex flex-col gap-1">
            {data.tips.map((tip) => (
              <li key={tip} className="text-sm text-fg">
                • {tip}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* AI analysis — embedded (Pro-gated). Replaces the old standalone
          /cr/coach screen. */}
      <div className="mt-auto flex flex-col gap-3">
        {isPro ? (
          <>
            {!analysisData && (
              <button
                type="button"
                onClick={onAnalyse}
                disabled={!tag || analysing}
                className="flex items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
              >
                <Sparkles className={analysing ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'} />
                {analysing ? t('analysis.thinking') : t('deckCheck.askAi')}
              </button>
            )}
            {!!analysisError && (
              <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
                {coachAnalysisError(analysisError, t)}
              </p>
            )}
            {analysisData?.analysis && (
              <CoachAnalysis analysis={analysisData.analysis} usage={analysisData.usage} />
            )}
          </>
        ) : (
          <Paywall feature={t('coach.title')} />
        )}
      </div>
    </div>
  );
}
